<template>
  <div class="menu-breadcrumbs__wrap">
    <div class="menu-breadcrumbs">
      <template v-if="isVisibleBreadcrumbProject">
        <MenuBreadcrumbsItem
          v-if="breadcrumbProject"
          :breadcrumb="breadcrumbProject"
          :no-route="noRoute"
          :color="color"
        />
      </template>
      <MenuBreadcrumbsItem
        v-else-if="breadcrumbs"
        :breadcrumb="breadcrumbs"
        :settings="settings"
        :no-route="noRoute"
        :color="color"
      />
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed, onMounted, onUnmounted, PropType, ref, watch } from 'vue';
import {
  BreadcrumbProjectModel,
  GroupModel,
  GroupUpdateNameEventResponseModel,
  SettingsBreadcrumbsModel,
  UpdateQueueNameModel,
} from './models';
import { MenuBreadcrumbsAdapterService } from './adapter';
import { NamespaceModel } from './models';
import { pending } from './utils';
import { MenuBreadcrumbsItem } from './components';
import { ProjectModel } from '@/models';
import { MenuBreadcrumbsColorEnum } from './enums';
import { UpdateNamespaceInfo } from './types';

const props = defineProps({
  /**Настройки */
  settings: {
    type: Object as PropType<SettingsBreadcrumbsModel>,
  },
  /**Проект */
  project: {
    type: Object as PropType<ProjectModel>,
  },
  /**Не использовать роуты */
  noRoute: {
    type: Boolean,
    default: false,
  },
  /**Цвет */
  color: {
    type: String as PropType<MenuBreadcrumbsColorEnum>,
  },
});

const adapter = new MenuBreadcrumbsAdapterService();

/**Хлебные крошки */
const breadcrumbs = ref<NamespaceModel>();

/**Проект */
const breadcrumbProject = computed(() =>
  props.project
    ? new BreadcrumbProjectModel({
        Id: Number(props.project?.Id),
        Name: props.project?.Name,
        ImageUrl: props.project?.ImageUrl,
        PreviewColor: props.project?.PreviewColor,
      })
    : undefined,
);

/**Отобразить хлебную крошку проекта */
const isVisibleBreadcrumbProject = computed(
  () => !(props.settings?.QueueId || props.settings?.GroupId || props.settings?.NamespaceId),
);

const isLoading = ref(false);

const pendingGetBreadcrumbs = pending(async (settings: SettingsBreadcrumbsModel) => {
  const resp = await adapter.getBreadcrumbs(settings);
  if (resp.IsSuccess) breadcrumbs.value = resp.Value;
});

const getBreadcrumbs = async (settings?: SettingsBreadcrumbsModel) => {
  isLoading.value = true;
  if (settings.GroupId || settings.QueueId || settings.NamespaceId) {
    await pendingGetBreadcrumbs(settings);
  }
  isLoading.value = false;
};

watch(
  () => props.settings,
  () => {
    if (props.settings) {
      getBreadcrumbs(props.settings);
    }
  },
  { deep: true },
);

/**При обновлении неймспейса */
const onUpdateNamespace = async (data: UpdateNamespaceInfo) => {
  if (breadcrumbs.value.Id === data.Id) {
    breadcrumbs.value = Object.assign(
      breadcrumbs.value,
      new NamespaceModel({
        ...breadcrumbs.value,
        Id: data.Id,
        Name: data.Name,
        IconId: data.IconId,
        IconUrl: data.IconUrl,
        Color: data.Color,
        ColorId: data.ColorId,
      }),
    );
  }
};
/** Обновить название очереди */
const updateQueueName = (
  data: UpdateQueueNameModel,
  group: NamespaceModel | GroupModel = breadcrumbs.value,
) => {
  if (!group) return;

  if (group.QueueTask?.Id === data.QueueId) {
    group.QueueTask.Name = data.Name;
  }
  if (group.Group) {
    updateQueueName(data, group.Group);
  }
};
/**Обновить название папки */
const updateGroupName = (
  data: GroupUpdateNameEventResponseModel,
  group: NamespaceModel | GroupModel = breadcrumbs.value,
) => {
  if (!group) return;

  if (group?.Id === data.GroupId) {
    group.Name = data.Name;
  }
  if (group.Group) {
    updateGroupName(data, group.Group);
  }
};

onMounted(() => {
  if (props.settings) {
    getBreadcrumbs(props.settings);
  }
  adapter.UpdateNamespaceEvent.on(onUpdateNamespace);
  adapter.UpdateQueueEvent.on(updateQueueName);
  adapter.OnUpdateNameGroupEvent.on(updateGroupName);
});

onUnmounted(() => {
  adapter.UpdateNamespaceEvent.removeListener(onUpdateNamespace);
  adapter.UpdateQueueEvent.removeListener(updateQueueName);
  adapter.OnUpdateNameGroupEvent.removeListener(updateGroupName);
});
</script>

<style lang="scss" scoped>
.menu-breadcrumbs {
  &__wrap {
    min-height: 1.5rem;
  }
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  row-gap: 0.5rem;
}
</style>
